import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen, Plus, Search, Github, ExternalLink, Users, Calendar, X, Edit2, Trash2, UserPlus, Lock, CheckCircle, MessageSquare } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import './AdminPages.css';

const STATUS_BADGE = { Planning:'badge-gray', 'In Progress':'badge-info', Review:'badge-warning', Completed:'badge-success' };

const emptyForm = { name:'', client:'', status:'Planning', deadline:'', tech:'', github:'', description:'', mentorId:'' };

export default function ProjectsPage() {
  const { data, commitData, showToast } = useApp();
  const { can, isFounder, isMentor, role, user } = useAuth();

  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('All');
  const [selected, setSelected]   = useState(null);
  const [showAdd, setShowAdd]     = useState(false);
  const [showEdit, setShowEdit]   = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [assignProjectId, setAssignProjectId] = useState(null);
  const [selectedEmpIds, setSelectedEmpIds]   = useState([]);

  // Filter projects based on role
  const visibleProjects = data.projects.filter(p => {
    if (can('canViewAllProjects')) return true;
    if (role === 'employee') return (p.team || []).map(String).includes(String(user?.employeeId));
    return true;
  });

  const filtered = visibleProjects.filter(p => {
    const s = p.name.toLowerCase().includes(search.toLowerCase()) || p.client.toLowerCase().includes(search.toLowerCase());
    const f = filter === 'All' || p.status === filter;
    return s && f;
  });

  const getEmpName = (id) => data.employees.find(e => String(e.id) === String(id))?.name || 'Unknown';

  /* ── ADD PROJECT ── */
  const handleAdd = async () => {
    if (!form.name || !form.client) { showToast('Project name and client are required','error'); return; }
    const mentorId = form.mentorId || data.employees.find(e => e.role === 'mentor')?.id || data.employees[0]?.id;
    const proj = {
      id: String(Date.now()), ...form,
      team: [],
      progress: 0,
      tech: (form.tech || '').split(',').map(t=>t.trim()).filter(Boolean),
      mentorId,
    };
    try {
      await commitData('projects', [...data.projects, proj]);
      showToast('Project created successfully');
      setShowAdd(false); setForm(emptyForm);
    } catch {}
  };

  /* ── EDIT PROJECT ── */
  const openEdit = (p, e) => {
    e.stopPropagation();
    setEditTarget(p);
    setForm({ ...p, tech: (p.tech || []).join(', ') });
    setShowEdit(true);
  };
  const handleEdit = async () => {
    if (!form.name) return;
    try {
      await commitData('projects', data.projects.map(p => p.id === editTarget.id
        ? { ...p, ...form, tech: (form.tech || '').split(',').map(t=>t.trim()).filter(Boolean) }
        : p));
      showToast('Project updated');
      setShowEdit(false); setEditTarget(null);
    } catch {}
  };

  /* ── DELETE PROJECT ── */
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try {
      await commitData('projects', data.projects.filter(p => p.id !== id));
      showToast('Project deleted', 'warning');
      if (selected?.id === id) setSelected(null);
    } catch {}
  };

  /* ── ASSIGN EMPLOYEES ── */
  const openAssign = (p, e) => {
    e.stopPropagation();
    setAssignProjectId(p.id);
    setSelectedEmpIds((p.team || []).map(String));
    setShowAssign(true);
  };
  const toggleEmp = (id) => setSelectedEmpIds(prev => {
    const sid = String(id);
    return prev.map(String).includes(sid) ? prev.filter(x => String(x) !== sid) : [...prev, sid];
  });
  const saveAssign = async () => {
    try {
      await commitData('projects', data.projects.map(p => p.id === assignProjectId ? { ...p, team: selectedEmpIds.map(String) } : p));
      showToast('Team updated successfully');
      setShowAssign(false);
    } catch {}
  };

  const assignableEmployees = data.employees.filter(e => e.role === 'employee');
  const canAssign = can('canAssignEmployee');  // Only mentor + founder
  const canCreate = can('canCreateProject');
  const canEdit   = can('canEditProject');
  const canDelete = can('canDeleteProject');   // Founder only

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">{filtered.length} project{filtered.length!==1?'s':''} · {data.projects.filter(p=>p.status!=='Completed').length} active</p>
        </div>
        <div className="page-actions">
          {canCreate && <button className="btn-gold" onClick={()=>{
            const mid = data.employees.find(e=>e.role==='mentor')?.id || data.employees[0]?.id || '';
            setForm({ ...emptyForm, mentorId: mid });
            setShowAdd(true);
          }}><Plus size={15}/> New Project</button>}
          {!canCreate && <span className="badge badge-gray"><Lock size={10}/> View Only</span>}
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-box">
          <Search size={15}/>
          <input className="search-input" placeholder="Search projects..." value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <div className="filter-tabs">
          {['All','Planning','In Progress','Review','Completed'].map(s=>(
            <button key={s} className={`filter-tab ${filter===s?'active':''}`} onClick={()=>setFilter(s)}>{s}</button>
          ))}
        </div>
      </div>

      <div className="cards-grid">
        {filtered.map(p => (
          <div key={p.id} className="project-card" onClick={()=>setSelected(p)}>
            <div className="project-card-header">
              <div className="project-icon"><FolderOpen size={16}/></div>
              <div style={{ display:'flex',gap:6,alignItems:'center' }}>
                <span className={`badge ${STATUS_BADGE[p.status]}`}>{p.status}</span>
                {/* Founder can edit/delete; mentor can edit */}
                {canEdit && (
                  <button className="btn-ghost" style={{ padding:'4px',border:'none' }} onClick={e=>openEdit(p,e)}><Edit2 size={12}/></button>
                )}
                {canDelete && (
                  <button className="btn-ghost" style={{ padding:'4px',border:'none',color:'var(--danger)' }} onClick={e=>handleDelete(p.id,e)}><Trash2 size={12}/></button>
                )}
              </div>
            </div>
            <p className="project-card-name">{p.name}</p>
            <p className="project-card-client">{p.client}</p>
            <div>
              <div className="project-progress-row"><span style={{ fontSize:'0.72rem',color:'var(--text-dim)' }}>Progress</span><span className="progress-pct">{p.progress}%</span></div>
              <div className="progress-track"><div className="progress-fill" style={{ width:`${p.progress}%` }}/></div>
            </div>
            <div className="tech-tags">{(p.tech||[]).slice(0,3).map(t=><span key={t} className="tech-tag">{t}</span>)}{(p.tech||[]).length>3&&<span className="tech-tag">+{(p.tech||[]).length-3}</span>}</div>
            <div className="project-card-footer">
              <span className="project-card-footer-item"><Users size={12}/>{(p.team||[]).length} members</span>
              {p.deadline && <span className="project-card-footer-item"><Calendar size={12}/>{new Date(p.deadline).toLocaleDateString('en-ZA',{day:'numeric',month:'short'})}</span>}
              <Link className="btn-ghost" style={{ padding:'4px 8px',fontSize:'0.7rem',marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:4,textDecoration:'none' }} to={`/admin/chat?project=${encodeURIComponent(p.id)}`} onClick={e=>e.stopPropagation()} title="Open chat">
                <MessageSquare size={11}/>
              </Link>
              {canAssign && (
                <button className="btn-outline" style={{ padding:'4px 10px',fontSize:'0.7rem' }} onClick={e=>openAssign(p,e)}>
                  <UserPlus size={11}/> Assign
                </button>
              )}
            </div>
          </div>
        ))}
        {canCreate && (
          <div className="add-card" onClick={()=>{
            const mid = data.employees.find(e=>e.role==='mentor')?.id || data.employees[0]?.id || '';
            setForm({ ...emptyForm, mentorId: mid });
            setShowAdd(true);
          }}>
            <Plus size={26}/><span>New Project</span>
          </div>
        )}
      </div>

      {/* ─── PROJECT DETAIL MODAL ─── */}
      {selected && (
        <div className="modal-overlay" onClick={()=>setSelected(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <p className="modal-title">{selected.name}</p>
                <p className="modal-subtitle">{selected.client}</p>
              </div>
              <button className="modal-close" onClick={()=>setSelected(null)}><X size={18}/></button>
            </div>
            <div className="detail-grid">
              <div className="detail-item"><span className="detail-label">Status</span><span className={`badge ${STATUS_BADGE[selected.status]}`}>{selected.status}</span></div>
              <div className="detail-item"><span className="detail-label">Progress</span><span className="detail-value">{selected.progress}%</span></div>
              <div className="detail-item"><span className="detail-label">Deadline</span><span className="detail-value">{selected.deadline ? new Date(selected.deadline).toLocaleDateString('en-ZA') : '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Mentor</span><span className="detail-value">{getEmpName(selected.mentorId)}</span></div>
            </div>
            {selected.description && <p style={{ fontSize:'0.875rem',color:'var(--text-muted)',lineHeight:1.7,marginBottom:16 }}>{selected.description}</p>}
            <p style={{ fontSize:'0.72rem',fontWeight:700,color:'var(--text-dim)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8 }}>Team Members ({(selected.team||[]).length})</p>
            <div style={{ display:'flex',flexWrap:'wrap',gap:6,marginBottom:16 }}>
              {(selected.team||[]).length===0 && <span style={{ color:'var(--text-dim)',fontSize:'0.82rem' }}>No team members assigned</span>}
              {(selected.team||[]).map(id => <span key={id} style={{ background:'var(--surface-2)',border:'1px solid var(--border-color)',borderRadius:20,padding:'4px 12px',fontSize:'0.8rem' }}>{getEmpName(id)}</span>)}
            </div>
            <p style={{ fontSize:'0.72rem',fontWeight:700,color:'var(--text-dim)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8 }}>Technologies</p>
            <div className="tech-tags" style={{ marginBottom:16 }}>{(selected.tech||[]).map(t=><span key={t} className="tech-tag">{t}</span>)}</div>
            {selected.github && <a href={selected.github} target="_blank" rel="noreferrer" style={{ display:'flex',alignItems:'center',gap:6,fontSize:'0.8rem',color:'var(--secondary-color)',marginBottom:20 }}><Github size={13}/>{selected.github}</a>}
            <div style={{ display:'flex',gap:10,justifyContent:'flex-end',flexWrap:'wrap' }}>
              <Link className="btn-outline" style={{ display:'inline-flex',alignItems:'center',gap:6,textDecoration:'none' }} to={`/admin/chat?project=${encodeURIComponent(selected.id)}`} onClick={()=>setSelected(null)}>
                <MessageSquare size={14}/> Open Chat
              </Link>
              {canAssign && <button className="btn-outline" onClick={e=>{openAssign(selected,e);setSelected(null)}}><UserPlus size={14}/> Assign Team</button>}
              {canEdit   && <button className="btn-gold"    onClick={e=>{openEdit(selected,e);setSelected(null)}}>Edit Project</button>}
            </div>
          </div>
        </div>
      )}

      {/* ─── ADD / EDIT MODAL ─── */}
      {(showAdd || showEdit) && (
        <div className="modal-overlay" onClick={()=>{setShowAdd(false);setShowEdit(false)}}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <p className="modal-title">{showAdd?'New Project':'Edit Project'}</p>
              <button className="modal-close" onClick={()=>{setShowAdd(false);setShowEdit(false)}}><X size={18}/></button>
            </div>
            <div className="modal-form">
              <div className="form-row">
                <div className="form-group"><label className="form-label">Project Name *</label><input className="form-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="E.g. E-Commerce Platform"/></div>
                <div className="form-group"><label className="form-label">Client *</label><input className="form-input" value={form.client} onChange={e=>setForm({...form,client:e.target.value})} placeholder="Client name"/></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Status</label>
                  <select className="form-input" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                    {['Planning','In Progress','Review','Completed'].map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Deadline</label><input className="form-input" type="date" value={form.deadline} onChange={e=>setForm({...form,deadline:e.target.value})}/></div>
              </div>
              <div className="form-group"><label className="form-label">Mentor</label>
                <select className="form-input" value={form.mentorId} onChange={e=>setForm({...form,mentorId:e.target.value})}>
                  {data.employees.filter(e=>e.role==='mentor'||e.role==='super_admin').map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Technologies (comma-separated)</label><input className="form-input" value={form.tech} onChange={e=>setForm({...form,tech:e.target.value})} placeholder="React, Node.js, MySQL"/></div>
              <div className="form-group"><label className="form-label">GitHub Repository</label><input className="form-input" value={form.github} onChange={e=>setForm({...form,github:e.target.value})} placeholder="https://github.com/..."/></div>
              <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows={3} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Project description..."/></div>
              {showEdit && isFounder && (
                <div className="form-group"><label className="form-label">Progress (%)</label><input className="form-input" type="number" min={0} max={100} value={form.progress||0} onChange={e=>setForm({...form,progress:parseInt(e.target.value)||0})}/></div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={()=>{setShowAdd(false);setShowEdit(false)}}>Cancel</button>
              <button className="btn-gold" onClick={showAdd?handleAdd:handleEdit}>{showAdd?'Create Project':'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── ASSIGN EMPLOYEES MODAL ─── */}
      {showAssign && (
        <div className="modal-overlay" onClick={()=>setShowAssign(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <p className="modal-title">Assign Team Members</p>
                <p className="modal-subtitle">{data.projects.find(p=>p.id===assignProjectId)?.name}</p>
              </div>
              <button className="modal-close" onClick={()=>setShowAssign(false)}><X size={18}/></button>
            </div>
            <div className="info-banner info-banner-gold" style={{ marginBottom:16 }}>
              <Lock size={14}/><span>Only Mentors and Founders can assign employees to projects.</span>
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:8,marginBottom:20 }}>
              {assignableEmployees.map(emp => (
                <div key={emp.id}
                  onClick={()=>toggleEmp(emp.id)}
                  style={{ display:'flex',alignItems:'center',gap:12,padding:'12px 14px',background:selectedEmpIds.map(String).includes(String(emp.id))?'rgba(201,162,39,0.08)':'var(--surface-2)',border:`1px solid ${selectedEmpIds.map(String).includes(String(emp.id))?'var(--secondary-color)':'var(--border-color)'}`,borderRadius:'var(--radius-md)',cursor:'pointer',transition:'var(--transition)' }}>
                  <div className="av av-sm av-gold">{emp.avatar}</div>
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:'0.875rem',fontWeight:600 }}>{emp.name}</p>
                    <p style={{ fontSize:'0.75rem',color:'var(--text-muted)' }}>{emp.title} · {emp.dept}</p>
                  </div>
                  <div style={{ width:20,height:20,borderRadius:4,border:`2px solid ${selectedEmpIds.map(String).includes(String(emp.id))?'var(--secondary-color)':'var(--border-color)'}`,background:selectedEmpIds.map(String).includes(String(emp.id))?'var(--secondary-color)':'transparent',display:'flex',alignItems:'center',justifyContent:'center' }}>
                    {selectedEmpIds.map(String).includes(String(emp.id)) && <CheckCircle size={13} color="#000"/>}
                  </div>
                </div>
              ))}
            </div>
            <p style={{ fontSize:'0.78rem',color:'var(--text-muted)',marginBottom:16 }}>{selectedEmpIds.length} employee{selectedEmpIds.length!==1?'s':''} selected</p>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={()=>setShowAssign(false)}>Cancel</button>
              <button className="btn-gold" onClick={saveAssign}>Save Team</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
